import { useCallback, useEffect, useRef, useState } from "react";
import { LockKeyhole, LogOut, ShieldCheck } from "lucide-react";
import { toast } from "../../Utills/toast";
import PasswordInput from "../common/PasswordInput";
import { ButtonLoader } from "../common/Loader";
import { advancedBillingApi } from "../../services/advancedBillingApi";
import { roleMap } from "../../Utills/enums";
import "./CounterLock.css";

const tokenKey = "Admin-API-Token";
const activityKey = "counter-lock-last-activity";
const lockedKey = "counter-screen-locked";

const decodeToken = token => {
  try {
    const value = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(value.padEnd(Math.ceil(value.length / 4) * 4, "=")));
  } catch { return null; }
};

const storeSession = ({ token, refreshToken }) => {
  const payload = decodeToken(token);
  if (!payload) throw new Error("The unlocked session could not be read.");
  const identity = {
    restaurantId: payload.restaurantId, restaurantName: payload.restaurantName,
    restaurantAddress: payload.restaurantAddress, restaurantPhone: payload.restaurantPhone,
    restaurantEmail: payload.restaurantEmail, userId: payload.userId, roleId: payload.roleId,
    name: payload.name, userName: payload.userName, packageId: payload.packageId,
    preferredLanguage: payload.preferredLanguage || "en"
  };
  localStorage.setItem(tokenKey, token);
  localStorage.setItem("refreshToken", refreshToken);
  localStorage.setItem("admin_token_info", JSON.stringify(identity));
  if (payload.exp) localStorage.setItem("tokenExpiry", new Date(payload.exp * 1000).toISOString());
  return identity;
};

const CounterLock = ({ restaurantId }) => {
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [locked, setLocked] = useState(localStorage.getItem(lockedKey) === "1");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [pin, setPin] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const lastRecordedRef = useRef(0);
  const overlayRef = useRef(null);
  const currentIdentity = (() => {
    try { return JSON.parse(localStorage.getItem("admin_token_info") || "{}"); }
    catch { return {}; }
  })();

  useEffect(() => {
    if (!restaurantId) return undefined;
    let active = true;
    Promise.all([advancedBillingApi.getSensitiveActionSettings(), advancedBillingApi.getCounterLockUsers()])
      .then(([configuration, userList]) => {
        if (!active) return;
        const available = (userList || []).filter(user => user.hasPin);
        setSettings(configuration);
        setUsers(userList || []);
        const current = available.find(user => String(user.userId) === String(currentIdentity.userId));
        setSelectedUserId(String((current || available[0])?.userId || ""));
        if (!configuration?.enableCounterScreenLock) {
          localStorage.removeItem(lockedKey);
          setLocked(false);
        }
      })
      .catch(() => {});
    return () => { active = false; };
  }, [restaurantId, currentIdentity.userId]);

  const markActivity = useCallback(() => {
    if (locked || !settings?.enableCounterScreenLock) return;
    const now = Date.now();
    if (now - lastRecordedRef.current < 2000) return;
    lastRecordedRef.current = now;
    localStorage.setItem(activityKey, String(now));
  }, [locked, settings?.enableCounterScreenLock]);

  useEffect(() => {
    if (!settings?.enableCounterScreenLock || !users.some(user => user.hasPin)) return undefined;
    if (!localStorage.getItem(activityKey)) localStorage.setItem(activityKey, String(Date.now()));
    const lockIfIdle = () => {
      const lastActivity = Number(localStorage.getItem(activityKey)) || Date.now();
      const timeout = Math.max(1, Number(settings.counterScreenLockMinutes) || 10) * 60 * 1000;
      if (Date.now() - lastActivity >= timeout) {
        localStorage.setItem(lockedKey, "1");
        setLocked(true);
      }
    };
    const events = ["pointerdown", "keydown", "touchstart", "mousemove"];
    events.forEach(name => window.addEventListener(name, markActivity, { passive: true }));
    const visibility = () => document.visibilityState === "visible" ? lockIfIdle() : markActivity();
    document.addEventListener("visibilitychange", visibility);
    const timer = window.setInterval(lockIfIdle, 15000);
    lockIfIdle();
    return () => {
      events.forEach(name => window.removeEventListener(name, markActivity));
      document.removeEventListener("visibilitychange", visibility);
      window.clearInterval(timer);
    };
  }, [markActivity, settings, users]);

  useEffect(() => {
    if (!locked) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [locked]);

  useEffect(() => {
    const overlay = overlayRef.current;
    const viewport = window.visualViewport;
    if (!locked || !settings?.enableCounterScreenLock || !overlay || !viewport) return undefined;

    const syncViewport = () => {
      overlay.style.setProperty("--counter-lock-viewport-height", `${viewport.height}px`);
      overlay.style.setProperty("--counter-lock-viewport-top", `${viewport.offsetTop}px`);
      overlay.classList.toggle("is-keyboard-open", window.innerHeight - viewport.height > 120);
    };

    syncViewport();
    viewport.addEventListener("resize", syncViewport);
    viewport.addEventListener("scroll", syncViewport);
    window.addEventListener("resize", syncViewport);
    return () => {
      viewport.removeEventListener("resize", syncViewport);
      viewport.removeEventListener("scroll", syncViewport);
      window.removeEventListener("resize", syncViewport);
    };
  }, [locked, settings?.enableCounterScreenLock]);

  const unlock = async event => {
    event.preventDefault();
    if (!selectedUserId) return toast.error("Ask an Admin or Manager to set an employee counter PIN.");
    try {
      setUnlocking(true);
      const previousUserId = String(currentIdentity.userId || "");
      const session = await advancedBillingApi.unlockCounter(Number(selectedUserId), pin, localStorage.getItem("refreshToken"));
      const identity = storeSession(session);
      localStorage.removeItem(lockedKey);
      localStorage.setItem(activityKey, String(Date.now()));
      setPin("");
      setLocked(false);
      if (String(identity.userId) !== previousUserId) window.location.reload();
      else window.dispatchEvent(new StorageEvent("storage", { key: "admin_token_info" }));
    } catch (error) {
      setPin("");
      toast.error(error.message || "Invalid PIN.");
    } finally { setUnlocking(false); }
  };

  const logout = () => {
    sessionStorage.clear();
    localStorage.clear();
    window.location.href = "/";
  };

  if (!locked || !settings?.enableCounterScreenLock) return null;
  const selected = users.find(user => String(user.userId) === String(selectedUserId));
  return <div ref={overlayRef} className="counter-lock-overlay" role="dialog" aria-modal="true" aria-labelledby="counter-lock-title">
    <div className="counter-lock-card">
      <span className="counter-lock-icon"><LockKeyhole size={34} /></span>
      <div className="counter-lock-brand"><ShieldCheck size={17} /> {currentIdentity.restaurantName}</div>
      <h1 id="counter-lock-title">Counter locked</h1>
      <p>The counter was locked after inactivity. Choose your name and enter your employee PIN to continue.</p>
      <form onSubmit={unlock}>
        <label>Employee
          <select value={selectedUserId} onChange={event => { setSelectedUserId(event.target.value); setPin(""); }}>
            {users.filter(user => user.hasPin).map(user => <option key={user.userId} value={user.userId}>
              {user.name} · {roleMap[user.roleId] || "User"}
            </option>)}
          </select>
        </label>
        {selected && <div className="counter-lock-user">Unlocking as <strong>{selected.name}</strong></div>}
        <label>PIN
          <PasswordInput required inputMode="numeric" minLength="4" maxLength="8" pattern="[0-9]+"
            autoComplete="off" toggleLabel="counter PIN" placeholder="Enter 4–8 digit PIN"
            value={pin} onChange={event => setPin(event.target.value.replace(/\D/g, "").slice(0, 8))} />
        </label>
        <button className="counter-unlock-button" disabled={unlocking || !selectedUserId}>
          {unlocking ? <ButtonLoader label="Unlocking..." /> : "Unlock counter"}
        </button>
      </form>
      <button type="button" className="counter-logout-button" onClick={logout}><LogOut size={17} /> Sign out instead</button>
      <small>Your open page remains available after the same employee unlocks the counter.</small>
    </div>
  </div>;
};

export default CounterLock;

